<template>
    <div>
        <b-datatable
            ref="grid"
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            has-filter-selector
            item-key="id"
            :grid-title="gridTitle"
            qc-mode
            :keep-filters-prefix="gridTitle"
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                v-if="can('api.v1.astm-devices.store') && can('astm-devices.create')"
                                color="primary"
                                icon
                                v-bind="attrs"
                                :href="createItemUrl()"
                                v-on="on"
                            >
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('astm-devices::button.create') }}</span>
                    </v-tooltip>
                </div>
            </template>
            <template #filters="{ filters }">
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.name"
                        :label="t('astm-devices::grid.label.device_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.active"
                        :items="filtersOptions.status"
                        :label="t('astm-devices::label.status')"
                        :clearable="filters.filters.active !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.device"
                        :items="filtersOptions.device"
                        :label="'Device type'"
                        :clearable="filters.filters.active !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.pharmacy_sites`]="{ item }">
                <span v-for="(value, key) in item.pharmacy_sites" :key="key">
                    {{ value.site_name }}
                </span>
            </template>

            <template #[`item.name`]="{ item }">
                <div v-if="can('astm-devices.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.device`]="{ item }">
                <span>{{ item.device ? item.device.charAt(0).toUpperCase() + item.device.slice(1) : '' }}</span>
            </template>

            <template #[`item.last_active_at`]="{ item }">
                {{
                    item.last_active_at
                        ? moment(item.last_active_at).format($root.dateFormatWithHours)
                        : t('default::label.na')
                }}
            </template>
            <template #[`item.is_patient_mode`]="{ item }">
                <div v-if="!item.is_patient_mode && item.visible_in_qc_list">
                    <v-icon color="red">mdi-alert-octagon-outline</v-icon>
                    <span class="error--text">{{ t('astm-devices::status-qc-off') }}</span>
                </div>
            </template>

            <template #[`item.active`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            !!item.active
                                ? t('astm-devices::options.status.active')
                                : t('astm-devices::options.status.inactive')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.open_vpn_status`]="{ item }">
                <div class="grid-status">
                    <span v-text="t('astm-devices::openvpn-status.' + item.open_vpn_status)"></span>
                </div>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('astm-devices.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('api.v1.astm-devices.update')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-cogs</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('astm-devices::button.manage') }}</span>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item.device === 'pixcell' && can('api.v1.providers.manage-abnormality-monitoring')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="addToAutorejectList(item)"
                            >
                                <v-icon v-if="!item.monitors_abnormality" size="24">mdi-bank-plus</v-icon>
                                <v-icon v-else size="24">mdi-bank-remove</v-icon>
                            </v-btn>
                        </template>
                        <span v-if="!item.monitors_abnormality">{{
                            t('astm-devices::button.add_to_list_autoreject_abnormal_samples')
                        }}</span>
                        <span v-else>{{ t('astm-devices::button.remove_from_list_autoreject_abnormal_samples') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
        columns: {
            type: Array,
            required: true,
        },
        hiddenColumns: {
            type: Array,
            required: true,
        },
        gridTitle: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            filtersOptions: {
                device: [
                    {
                        text: t('astm-devices::options.status.all'),
                        value: null,
                    },
                    {
                        text: 'Pixcell',
                        value: 'pixcell',
                    },
                    {
                        text: 'Yumizen',
                        value: 'yumizen',
                    },
                ],
                status: [
                    {
                        text: t('astm-devices::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('astm-devices::options.status.active'),
                        value: 1,
                    },
                    {
                        text: t('astm-devices::options.status.inactive'),
                        value: 0,
                    },
                    // {
                    //     text: t('astm-devices::options.status.blocked'),
                    //     value: 'blocked',
                    // },
                    // {
                    //     text: t('astm-devices::options.status.authorized'),
                    //     value: 'authorized',
                    // },
                ],
            },
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('astm-devices.export-record-as-pdf', { astm_device: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        changeTab() {
            this.$refs.grid;
            if (!this.$refs.grid) {
                return;
            }
            this.$refs.grid.switchTab();
        },
        itemUrl(item) {
            return route('astm-devices.get', { id: item.id });
        },
        editItemUrl(item) {
            return route('astm-devices.get', { id: item.id });
        },
        addToAutorejectList(item) {
            axios
                .put(route('api.v1.providers.manage-abnormality-monitoring', { blood_result_provider: item.id }))
                .then((response) => {
                    item.monitors_abnormality = response.data.data.monitors_abnormality;
                    this.datatableKey++;
                    this.$root.notify('success', t('astm-devices::message.autoreject_list_updated'));
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                });
        },
        createItemUrl() {
            return route('astm-devices.create');
        },
    },
};
</script>
